import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrgConfigService } from '../../services/orgConfigService';
import { ShiftMaster } from '../../types/shiftRoster';
import {
  SHIFT_REPORT_TYPES,
  ShiftReportFilters,
  ShiftReportRecord,
  ShiftReportType,
  defaultFilters,
  emptyFilters,
  reportMeta,
} from '../../types/shiftReport';
import {
  ReportColumnDef,
  ShiftReportRow,
  applyFilters,
  buildRows,
  columnsFor,
  describeFilters,
  loadRecords,
  resolveRange,
  summarise,
  MONTH_LABELS,
  NO_DEPARTMENT,
  WEEKDAY_LABELS,
} from '../../services/shiftReportService';
/*
 * `import type` plus a dynamic import in the download handler, rather than a plain import.
 *
 * leaveReportExport pulls in jspdf, which probes for a canvas the moment it loads. Importing it
 * statically would put ~350KB of PDF writer into the chunk that renders the roster page and make
 * every test that mounts the page emit a jsdom canvas warning — for a dependency that is only
 * needed once somebody actually clicks Download. The xlsx writer has no dependencies, so it stays
 * static.
 */
import type { ExportColumn } from '../../services/leaveReportExport';
import { exportReportXlsx } from '../../services/xlsxExport';
import EmptyState from '../common/EmptyState';
import './ShiftReportsTab.css';

/**
 * Shift Roster → Reports.
 *
 * The flow the requirement lays out — Report Type → Apply Filters → Preview → Select Columns →
 * Download — is the order of the page, top to bottom, and each step's control sits in its own
 * numbered section so the page reads as that sequence rather than as a wall of inputs.
 *
 * Two things about how filtering works here, because they are not arbitrary:
 *
 *  - Filters are edited into a *draft* and committed by Apply Filters. Not because filtering is
 *    slow (it is a local array pass), but because the date range is the one filter that decides
 *    what has to be fetched, and a report that re-queried AppSync on every keystroke in "Employee
 *    Name" would be unusable. Committing all filters together also means the preview always
 *    corresponds to a filter set the Admin finished describing, which is what makes the row count
 *    above it trustworthy.
 *  - The preview paginates; the download never does. Every export is handed the full filtered row
 *    set, which is the rule leaveReportExport documents — a caller passing a page slice produces a
 *    file that looks complete and is not.
 */

/**
 * How many rows the preview will render at once.
 *
 * There is no Previous/Next any more: the table scrolls in its own box under a sticky header, so
 * paging through it in hundreds was a second navigation for something the scrollbar already did.
 *
 * A ceiling still exists, because the preview builds real DOM: one row here is a dozen cells, and
 * a year across a large directory is tens of thousands of rows — enough to lock the tab. It is
 * generous enough that no ordinary range reaches it, and when it does bite the page says so
 * outright rather than quietly ending the table. The download is never truncated.
 */
const PREVIEW_ROW_LIMIT = 2000;

/** Filters that decide what must be fetched. Any other change is a local re-filter. */
const rangeKey = (f: ShiftReportFilters): string =>
  `${f.dateFrom}|${f.dateTo}|${f.month}|${f.year}`;

const rosterYears = (): number[] => {
  const y = new Date().getFullYear();
  return [y - 2, y - 1, y, y + 1];
};

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, children }) => (
  <label className="srr-field">
    <span className="srr-field-label">
      {label}
      {hint && <span className="srr-field-hint" title={hint}>?</span>}
    </span>
    {children}
  </label>
);

interface ShiftReportsTabProps {
  shifts: ShiftMaster[];
  showToast: (message: string, type: 'success' | 'error') => void;
}

const ShiftReportsTab: React.FC<ShiftReportsTabProps> = ({ shifts, showToast }) => {
  const [reportType, setReportType] = useState<ShiftReportType>('DAILY');

  const [draft, setDraft] = useState<ShiftReportFilters>(defaultFilters);
  const [applied, setApplied] = useState<ShiftReportFilters>(defaultFilters);

  const [records, setRecords] = useState<ShiftReportRecord[]>([]);
  const [loadedShifts, setLoadedShifts] = useState<ShiftMaster[]>(shifts);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * No `degraded` state any more.
   *
   * It existed to warn that the assignment audit fields were not being served, which mattered only
   * to the Shift Change report — removed above. Nothing on the page reads status, rosterStatus,
   * previousShiftCode or changeReason now, so a warning about them would be about nothing. The
   * service still tracks the refusal, because it is what drives the query fallback.
   */
  /*
   * Employees whose assignments could not be read for this range.
   *
   * Held in state and shown beside the preview, because their rows are present and look ordinary
   * — built from the primary shift and the weekly off — while being unable to show an override
   * that exists. Somebody who worked a Saturday reads as having taken it off, and the only
   * honest response is to say which names cannot be trusted.
   */
  const [unreadable, setUnreadable] = useState<string[]>([]);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [teamOptions, setTeamOptions] = useState<string[]>(() => OrgConfigService.getTeams());

  // The range that produced `records`, so a filter change that does not move the range does not
  // refetch. A ref rather than state: it is bookkeeping about the fetch, not something rendered.
  const loadedRangeKey = useRef<string | null>(null);
  // Guards against a slow first request overwriting the results of a later, faster one.
  const requestSeq = useRef(0);

  useEffect(() => {
    const read = () => {
      setTeamOptions(OrgConfigService.getTeams());
    };
    OrgConfigService.refresh().finally(read);
    window.addEventListener('org-config-updated', read);
    return () => window.removeEventListener('org-config-updated', read);
  }, []);

  useEffect(() => { if (shifts.length) setLoadedShifts(shifts); }, [shifts]);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async (filters: ShiftReportFilters) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    setProgress({ done: 0, total: 0 });
    try {
      const result = await loadRecords(filters, {
        onProgress: (done, total) => {
          if (seq === requestSeq.current) setProgress({ done, total });
        },
      });
      if (seq !== requestSeq.current) return;
      setRecords(result.records);
      if (result.shifts.length) setLoadedShifts(result.shifts);
      setUnreadable(result.unreadable);
      loadedRangeKey.current = rangeKey(filters);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      console.error('[ShiftReports] Load error:', err);
      setRecords([]);
      setUnreadable([]);
      loadedRangeKey.current = null;
      setError(
        err?.errors?.[0]?.message || err?.message ||
        'Unable to load roster data for this range.'
      );
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setProgress(null);
      }
    }
  }, []);

  /*
   * First paint fetches the default month. Every later fetch is driven by Apply Filters.
   *
   * Deliberately mount-only. Depending on `applied` would refetch on every committed filter
   * change, including the ones that need no new data — the point of holding the fetched range in
   * `loadedRangeKey` is that narrowing by department or shift re-filters what is already in hand.
   * `fetchRecords` is a stable useCallback with no deps, so it cannot go stale here.
   */
  useEffect(() => {
    // `applied` here is its initial value — the default month — since nothing has committed yet.
    fetchRecords(applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const appliedRange = useMemo(() => resolveRange(applied), [applied]);
  const filteredRecords = useMemo(() => applyFilters(records, applied), [records, applied]);
  /*
   * How many people in the loaded range have no Department set.
   *
   * Printed on the option itself, because a blank Department is common (it is an optional
   * UserProfiles field) and invisible: an Admin filtering by department got fewer rows than they
   * expected with nothing saying that the missing people simply have no department recorded.
   * Counted over employees rather than employee-days — "17" reads as people, not as row-days.
   */
  const noDepartmentCount = useMemo(() => {
    const ids = new Set<string>();
    for (const r of records) if (!r.team) ids.add(r.employeeId);
    return ids.size;
  }, [records]);
  const rows = useMemo(() => buildRows(filteredRecords, reportType), [filteredRecords, reportType]);
  const summary = useMemo(() => summarise(filteredRecords), [filteredRecords]);
  const meta = reportMeta(reportType);

  const availableColumns = useMemo(() => columnsFor(reportType), [reportType]);

  /**
   * Which columns are ticked, keyed by report type.
   *
   * Per type rather than one global set: the summary reports and the detail reports share no
   * column ids, so a single set would empty itself every time the report type changed.
   */
  const [columnChoice, setColumnChoice] = useState<Record<string, string[]>>({});

  const selectedColumnIds = useMemo(() => {
    const stored = columnChoice[reportType];
    if (stored) return stored;
    return availableColumns.filter(c => c.on).map(c => c.id);
  }, [columnChoice, reportType, availableColumns]);

  const selectedColumns = useMemo(
    // Ordered by the registry, not by click order, so the table and the export keep a stable
    // column order however the Admin ticked them.
    () => availableColumns.filter(c => selectedColumnIds.includes(c.id)),
    [availableColumns, selectedColumnIds]
  );

  const setSelected = (ids: string[]) =>
    setColumnChoice(prev => ({ ...prev, [reportType]: ids }));

  const toggleColumn = (id: string) =>
    setSelected(
      selectedColumnIds.includes(id)
        ? selectedColumnIds.filter(c => c !== id)
        : [...selectedColumnIds, id]
    );


  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(applied),
    [draft, applied]
  );

  /**
   * Whether the pending range is wide enough to be worth warning about.
   *
   * Assignments are read per employee, so the cost scales with employees × pagination, not with
   * days alone — a whole year across the directory is a few hundred requests. Said before Apply
   * rather than after, because the useful moment to know is while the range can still be narrowed.
   * A quarter is the threshold: any single month or quarter is routine.
   */
  const draftRange = useMemo(() => resolveRange(draft), [draft]);
  const draftDays = useMemo(
    () => Math.round((Date.parse(draftRange.to) - Date.parse(draftRange.from)) / 86400000) + 1,
    [draftRange]
  );
  const wideRange = draftDays > 92;

  const visibleRows = useMemo(() => rows.slice(0, PREVIEW_ROW_LIMIT), [rows]);
  const truncated = rows.length > PREVIEW_ROW_LIMIT;

  // ── Actions ─────────────────────────────────────────────────────────────────

  const set = <K extends keyof ShiftReportFilters>(key: K, value: ShiftReportFilters[K]) =>
    setDraft(prev => ({ ...prev, [key]: value }));

  const handleApply = () => {
    setApplied(draft);
    if (rangeKey(draft) !== loadedRangeKey.current) fetchRecords(draft);
  };

  /** Back to the page's opening state: the current month, no other constraint. */
  const handleReset = () => {
    const next = defaultFilters();
    setDraft(next);
    setApplied(next);
    if (rangeKey(next) !== loadedRangeKey.current) fetchRecords(next);
  };

  /**
   * Every constraint removed, including the month — which widens the range to the whole year, so
   * it refetches. Distinct from Reset, which returns to the default month.
   */
  const handleClearAll = () => {
    const next = emptyFilters();
    setDraft(next);
    setApplied(next);
    if (rangeKey(next) !== loadedRangeKey.current) fetchRecords(next);
  };

  const exportRequest = useCallback(() => {
    const columns: ExportColumn<ShiftReportRow>[] = selectedColumns.map((c: ReportColumnDef) => ({
      header: c.header,
      value: c.value,
      numeric: c.numeric,
    }));
    return {
      title: meta.label,
      columns,
      // The whole filtered set, deliberately not `pageRows`.
      rows,
      meta: [
        ...describeFilters(applied, appliedRange),
        { label: 'Rows', value: String(rows.length) },
      ],
    };
  }, [selectedColumns, meta.label, rows, applied, appliedRange]);

  /**
   * The applied constraints as one readable clause, minus the date range.
   *
   * Built from describeFilters — the same list written into every downloaded file — so the reason
   * the preview gives for being empty and the record of what was asked for cannot disagree. The
   * range is dropped because the empty state states it separately.
   */
  const activeFilterSummary = useMemo(() => {
    const parts = describeFilters(applied, appliedRange)
      .filter(f => f.label !== 'Date range')
      .map(f => `${f.label} = ${f.value}`);
    return parts.join(', ');
  }, [applied, appliedRange]);

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (format: 'xlsx' | 'csv' | 'pdf') => {
    if (!rows.length) {
      showToast('Nothing to download — no rows match these filters.', 'error');
      return;
    }
    if (!selectedColumns.length) {
      showToast('Select at least one column before downloading.', 'error');
      return;
    }
    setDownloading(true);
    try {
      const request = exportRequest();
      if (format === 'xlsx') {
        exportReportXlsx(request);
      } else {
        const { exportReportCsv, exportReportPdf } = await import('../../services/leaveReportExport');
        if (format === 'csv') exportReportCsv(request);
        else exportReportPdf(request);
      }
      showToast(`${meta.label} downloaded (${rows.length} rows).`, 'success');
    } catch (err) {
      console.error('[ShiftReports] Export error:', err);
      showToast('Could not generate the file.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  /*
   * The tiles, grouped so the arithmetic between them is visible.
   *
   * They were a flat row, and the numbers looked unrelated: "Total Shifts 185" sat beside
   * "General 143" with nothing saying that 185 *is* the four shift bands added up, or that the
   * remaining days are accounted for by the offs, holidays, leave and unassigned tiles. Every
   * figure was right and the set still read as arbitrary.
   *
   * Now: how many employee-days the report covers, then the days that were worked broken into
   * bands, then the days that were not. The caption underneath states the two sums outright, so a
   * reader can check the split rather than having to infer it.
   */
  const totalDays = filteredRecords.length;
  const notWorked = summary.weeklyOff + summary.holiday + summary.onLeave + summary.unassigned;

  /*
   * Three headline figures, then the mix — rather than eleven tiles of equal weight.
   *
   * Every number here was already correct; the set simply did not read as anything. Eleven
   * identically-sized tiles say that headcount, employee-days and "Evening" are equally important,
   * so the eye has nowhere to land and the two totals that actually explain the report were lost
   * among nine breakdown figures. A sentence underneath spelled the arithmetic out in prose, which
   * is what a layout has to do when its own structure does not.
   *
   * So: the scale of the report first, then the mix as two labelled groups whose headers carry
   * their subtotals. The grouping states the identity that the prose used to —
   * employee-days = worked + not worked — and the band counts become secondary, which is what they
   * are. No chart: eight bands all carry meaning, and eight competing fills cannot be told apart
   * under colour-vision deficiency however they are ordered. The count is the encoding; the dot
   * beside each label only ties it to the colour the same band wears on the roster grid.
   */
  const kpis: { label: string; value: number; hint: string }[] = [
    { label: 'Employees', value: summary.totalEmployees, hint: 'Distinct employees with at least one day in range' },
    { label: 'Employee-days', value: totalDays, hint: 'Rows the filters selected — employees × days' },
    { label: 'Days worked', value: summary.totalShifts, hint: 'Days on a shift: Morning + General + Evening + Night' },
  ];

  /*
   * Only the worked bands carry a colour dot, and for a reason rather than for symmetry: those four
   * are shifts, and the dot matches the colour the same shift wears on the roster grid. A weekly
   * off, a holiday, leave and an unrostered day are not shifts and have no colour there, so a dot
   * on them would invent an identity — and eight dots in a row cannot be told apart under
   * colour-vision deficiency in any order (red and pink land 2 points under the floor). The four
   * that remain clear every gate in both themes.
   */
  const bandGroups: { title: string; total: number; bands: { label: string; value: number; tone?: string }[] }[] = [
    {
      title: 'Worked',
      total: summary.totalShifts,
      bands: [
        { label: 'Morning', value: summary.morning, tone: 'morning' },
        { label: 'General', value: summary.general, tone: 'general' },
        { label: 'Evening', value: summary.evening, tone: 'evening' },
        { label: 'Night', value: summary.night, tone: 'night' },
      ],
    },
    {
      title: 'Not worked',
      total: notWorked,
      bands: [
        { label: 'Weekly offs', value: summary.weeklyOff },
        { label: 'Holidays', value: summary.holiday },
        { label: 'On leave', value: summary.onLeave },
        { label: 'No shift set', value: summary.unassigned },
      ],
    },
    // No Changed or OT-hours figure: both measures were removed from the filters and the columns,
    // and a headline number for something the report cannot then show or export is a dead end.
  ];

  return (
    <div className="srr">
      {/* 1 ─ Report type */}
      <section className="srr-card">
        <header className="srr-card-head">
          <span className="srr-step">1</span>
          <div>
            <h2>Select Report Type</h2>
            <p>{meta.description}</p>
          </div>
          <select
            className="srr-select srr-select--report"
            value={reportType}
            onChange={e => setReportType(e.target.value as ShiftReportType)}
            aria-label="Report type"
          >
            {SHIFT_REPORT_TYPES.map(r => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </header>

      </section>

      {/* 2 ─ Filters */}
      <section className="srr-card">
        <header className="srr-card-head">
          <span className="srr-step">2</span>
          <div>
            <h2>Apply Filters</h2>
            <p>
              Every filter is combined — Department + Shift + date range narrow together.
              Showing <strong>{appliedRange.label}</strong>.
            </p>
          </div>
          <button
            type="button"
            className="srr-btn srr-btn--ghost"
            onClick={() => setFiltersOpen(o => !o)}
            aria-expanded={filtersOpen}
          >
            {filtersOpen ? 'Hide' : 'Show'} filters
          </button>
        </header>

        {filtersOpen && (
          <>
            <div className="srr-group">
              <h3 className="srr-group-title">Period</h3>
              <div className="srr-grid">
                <Field label="Date From"><input type="date" className="srr-input" value={draft.dateFrom} onChange={e => set('dateFrom', e.target.value)} /></Field>
                <Field label="Date To"><input type="date" className="srr-input" value={draft.dateTo} onChange={e => set('dateTo', e.target.value)} /></Field>
                <Field label="Month" hint="Ignored when both Date From and Date To are set.">
                  <select className="srr-select" value={draft.month} onChange={e => set('month', Number(e.target.value))}>
                    <option value={-1}>Whole year</option>
                    {MONTH_LABELS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Year">
                  <select className="srr-select" value={draft.year} onChange={e => set('year', Number(e.target.value))}>
                    {rosterYears().map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <div className="srr-group">
              <h3 className="srr-group-title">Employee</h3>
              <div className="srr-grid">
                <Field label="Employee Name"><input className="srr-input" placeholder="Name or email" value={draft.employeeName} onChange={e => set('employeeName', e.target.value)} /></Field>
                <Field label="Employee ID"><input className="srr-input" placeholder="HR employee number" value={draft.employeeCode} onChange={e => set('employeeCode', e.target.value)} /></Field>
                <Field label="Department" hint="The Team / Department list HR maintains in Organisation Setup.">
                  <select className="srr-select" value={draft.team} onChange={e => set('team', e.target.value)}>
                    <option value="">All departments</option>
                    {teamOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    {/* Reachable on purpose — see NO_DEPARTMENT. Without it the only way to find
                        the people with no department was to clear the filter entirely. */}
                    <option value={NO_DEPARTMENT}>
                      No department set{noDepartmentCount ? ` (${noDepartmentCount})` : ''}
                    </option>
                  </select>
                </Field>
                <Field label="Employee Status">
                  <select className="srr-select" value={draft.employeeStatus} onChange={e => set('employeeStatus', e.target.value)}>
                    <option value="ALL">Active &amp; inactive</option>
                    <option value="ACTIVE">Active only</option>
                    <option value="INACTIVE">Inactive only</option>
                  </select>
                </Field>
              </div>
            </div>

            <div className="srr-group">
              <h3 className="srr-group-title">Shift &amp; Day</h3>
              <div className="srr-grid">
                <Field label="Shift">
                  <select className="srr-select" value={draft.shiftCode} onChange={e => set('shiftCode', e.target.value)}>
                    <option value="">All shifts</option>
                    {loadedShifts.map(s => (
                      <option key={s.id || s.shiftCode} value={s.shiftCode}>{s.shiftCode} — {s.shiftName}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Specific Day">
                  <select className="srr-select" value={draft.specificDay} onChange={e => set('specificDay', e.target.value)}>
                    <option value="">Any day</option>
                    {WEEKDAY_LABELS.map((d, i) => <option key={d} value={String(i)}>{d}</option>)}
                  </select>
                </Field>
                <Field
                  label="Working Day / Holiday"
                  hint={
                    'Working days only: excludes weekly offs, public holidays and approved leave. '
                    + 'Days with no shift assigned are kept — those are working days nobody rostered. '
                    + 'Public holidays only: every holiday in range, whether it was worked or not.'
                  }
                >
                  <select className="srr-select" value={draft.holidayKind} onChange={e => set('holidayKind', e.target.value)}>
                    <option value="ALL">All days</option>
                    <option value="WORKING">Working days only</option>
                    <option value="HOLIDAY">Public holidays only</option>
                  </select>
                </Field>
              </div>
            </div>

            {wideRange && (
              <p className="srr-notice srr-notice--warn">
                <strong>{draftRange.label}</strong> is {draftDays} days. The roster is read per
                employee, so a range this wide takes noticeably longer to load — narrow it to a
                month if you can.
              </p>
            )}

            <div className="srr-actions">
              <button type="button" className="srr-btn srr-btn--primary" onClick={handleApply} disabled={loading}>
                Apply Filters
              </button>
              <button type="button" className="srr-btn srr-btn--outline" onClick={handleReset} disabled={loading}>
                Reset Filters
              </button>
              <button type="button" className="srr-btn srr-btn--outline" onClick={handleClearAll} disabled={loading}>
                Clear All
              </button>
              {dirty && <span className="srr-dirty">Unapplied changes — press Apply Filters</span>}
            </div>
          </>
        )}
      </section>

      {/* 3 ─ Preview */}
      <section className="srr-card">
        <header className="srr-card-head">
          <span className="srr-step">3</span>
          <div>
            <h2>Preview</h2>
            <p>
              {loading
                ? 'Loading roster data…'
                : `${rows.length.toLocaleString()} row${rows.length === 1 ? '' : 's'} in ${meta.label}.`}
            </p>
          </div>
          <div className="srr-head-actions">
            <button
              type="button"
              className="srr-btn srr-btn--outline"
              onClick={() => setColumnsOpen(o => !o)}
              aria-expanded={columnsOpen}
            >
              Select Columns
              <span className="srr-count">{selectedColumns.length}/{availableColumns.length}</span>
            </button>
          </div>
        </header>

        {columnsOpen && (
          <div className="srr-columns">
            <div className="srr-columns-head">
              <strong>Columns in the preview and the download</strong>
              <div className="srr-columns-actions">
                <button type="button" className="srr-link" onClick={() => setSelected(availableColumns.map(c => c.id))}>Select all</button>
                <button type="button" className="srr-link" onClick={() => setSelected([])}>Clear</button>
                <button type="button" className="srr-link" onClick={() => setSelected(availableColumns.filter(c => c.on).map(c => c.id))}>Defaults</button>
              </div>
            </div>
            <div className="srr-columns-grid">
              {availableColumns.map(c => (
                <label key={c.id} className="srr-check">
                  <input
                    type="checkbox"
                    checked={selectedColumnIds.includes(c.id)}
                    onChange={() => toggleColumn(c.id)}
                  />
                  <span>{c.header}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="srr-summary">
          <div className="srr-kpis">
            {kpis.map(k => (
              <div key={k.label} className="srr-kpi" title={k.hint}>
                <span className="srr-kpi-value">{k.value.toLocaleString()}</span>
                <span className="srr-kpi-label">{k.label}</span>
              </div>
            ))}
          </div>

          {/* Hidden when there is nothing to break down — a row of zeroes is not information. */}
          {totalDays > 0 && (
            <div className="srr-bands">
              {bandGroups.map(g => (
                <div key={g.title} className="srr-band-group">
                  <div className="srr-band-head">
                    <span className="srr-band-title">{g.title}</span>
                    {/* The subtotal sits in the header, so the two groups add up to Employee-days
                        above without a sentence having to say so. */}
                    <span className="srr-band-total">{g.total.toLocaleString()}</span>
                  </div>
                  <div className="srr-band-list">
                    {g.bands.map(b => (
                      <span
                        key={b.label}
                        className={`srr-chip${b.tone ? ` srr-chip--dot srr-chip--${b.tone}` : ''}`}
                      >
                        <span className="srr-chip-label">{b.label}</span>
                        <span className="srr-chip-value">{b.value.toLocaleString()}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* States the two sums outright, so the tiles can be checked rather than puzzled over. */}
        {totalDays > 0 && (
          <p className="srr-tiles-note">
            <strong>{totalDays.toLocaleString()}</strong> employee-days ={' '}
            <strong>{summary.totalShifts.toLocaleString()}</strong> worked
            {' '}(Morning {summary.morning} + General {summary.general} + Evening {summary.evening}
            {' '}+ Night {summary.night}) +{' '}
            <strong>{notWorked.toLocaleString()}</strong> not worked
            {' '}(weekly offs {summary.weeklyOff}, holidays {summary.holiday}, on leave{' '}
            {summary.onLeave}, no shift set {summary.unassigned}).
          </p>
        )}

        {/*
          Named, not counted. "2 employees could not be read" leaves everyone wondering whether
          they are one of them; the names make the gap checkable against the roster grid, which is
          exactly where someone would go to confirm it.
        */}
        {!loading && unreadable.length > 0 && (
          <p className="srr-notice srr-notice--warn">
            <strong>
              Shift assignments could not be read for {unreadable.length}{' '}
              {unreadable.length === 1 ? 'employee' : 'employees'}.
            </strong>{' '}
            {unreadable.slice(0, 6).join(', ')}
            {unreadable.length > 6 ? `, and ${unreadable.length - 6} more` : ''}.
            Their rows below fall back to the primary shift and the weekly off, so any override
            they worked is missing and every total here understates it. Reload to retry.
          </p>
        )}

        {loading && (
          <div className="srr-loading">
            <div className="srr-spinner" />
            <span>
              Reading assignments
              {progress && progress.total > 0 ? ` — ${progress.done} of ${progress.total} employees` : '…'}
            </span>
          </div>
        )}

        {!loading && error && (
          <div className="srr-notice srr-notice--error">
            <strong>Could not load the report.</strong> {error}
            <button type="button" className="srr-btn srr-btn--outline" onClick={() => fetchRecords(applied)}>Retry</button>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          /*
           * Says which filters did the excluding, and how much data they were applied to.
           *
           * "No rows match these filters" is indistinguishable from "the filters are not working"
           * — the complaint this replaces. Naming the constraints, and the employee-day count they
           * ran against, shows they were applied and points at the one to relax. The
           * range-only case is different: nothing was loaded at all, so no filter is at fault.
           */
          <EmptyState
            icon="search"
            title={
              records.length === 0
                ? `No roster data for ${appliedRange.label}`
                : 'Every row was excluded by a filter'
            }
            subtitle={
              records.length === 0
                ? 'Nothing is rostered in this range. Pick another month, or check Shift Details for the period.'
                : `${records.length.toLocaleString()} employee-days loaded for ${appliedRange.label}, `
                  + `and none of them match ${activeFilterSummary || 'the current filters'}.`
            }
            actionLabel="Reset Filters"
            onAction={handleReset}
          />
        )}

        {!loading && !error && rows.length > 0 && selectedColumns.length === 0 && (
          <p className="srr-notice srr-notice--warn">
            Every column is unticked, so there is nothing to show. Open <strong>Select Columns</strong> and
            choose at least one.
          </p>
        )}

        {!loading && !error && rows.length > 0 && selectedColumns.length > 0 && (
          <>
            <div className="srr-table-wrap">
              <table className="srr-table">
                <thead>
                  <tr>
                    {selectedColumns.map(c => (
                      <th key={c.id} className={c.numeric ? 'srr-num' : undefined}>{c.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(row => (
                    <tr key={row.key}>
                      {selectedColumns.map(c => {
                        const v = c.value(row);
                        return (
                          <td key={c.id} className={c.numeric ? 'srr-num' : undefined}>
                            {v === '' || v === null || v === undefined ? <span className="srr-dash">—</span> : v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Stated, never silent: a table that simply stops at a round number looks complete. */}
            {truncated && (
              <p className="srr-hint">
                Showing the first {PREVIEW_ROW_LIMIT.toLocaleString()} of{' '}
                {rows.length.toLocaleString()} rows — narrow the range to preview the rest. The
                download contains all {rows.length.toLocaleString()}.
              </p>
            )}
          </>
        )}
      </section>

      {/* 4 ─ Download */}
      <section className="srr-card">
        <header className="srr-card-head">
          <span className="srr-step">4</span>
          <div>
            <h2>Download Report</h2>
            <p>
              {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'} ·{' '}
              {selectedColumns.length} column{selectedColumns.length === 1 ? '' : 's'} ·{' '}
              filters and row count are written into the file.
            </p>
          </div>
        </header>

        <div className="srr-downloads">
          <button type="button" className="srr-btn srr-btn--primary" onClick={() => handleDownload('xlsx')} disabled={loading || downloading || !rows.length}>
            Excel (.xlsx)
          </button>
          <button type="button" className="srr-btn srr-btn--outline" onClick={() => handleDownload('csv')} disabled={loading || downloading || !rows.length}>
            CSV
          </button>
          <button type="button" className="srr-btn srr-btn--outline" onClick={() => handleDownload('pdf')} disabled={loading || downloading || !rows.length}>
            PDF
          </button>
          {downloading && <span className="srr-hint">Generating…</span>}
        </div>
        {selectedColumns.length > 12 && (
          <p className="srr-hint">
            {selectedColumns.length} columns will be cramped in the PDF — Excel or CSV keeps them readable.
          </p>
        )}
      </section>
    </div>
  );
};

export default ShiftReportsTab;
